import {BankAccountIdentifierType} from '@sollapay/enums';
import {
  BankBranchField,
  BankProviderField,
  FieldInput,
  FieldSelect,
  Icon
} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {AiFlashWrapper} from '@/features/ai-extraction';
import {
  generateBankAccountNumber,
  generateIsraeliIban,
  withDebugFieldChange
} from '@/features/debug-test-data';

import {isBankCheckAccountType} from '../utils/creditor-account.utils';

import type {CreditorAccountType} from '../utils/creditor-account.utils';
import type {useFieldAiProps} from '@/features/ai-extraction';
import type {BankBranchInfo, BankProviderInfo, FieldSelectOption} from '@sollapay/ui/components';

const PREFIX = 'view.payouts.create.details';

export interface BankAccountFieldsProps {
  accountType: CreditorAccountType;
  /** The selected bank provider's id — see `PaymentDetailsStep.CreatePayoutFormValues.bankProviderId`. */
  bankProviderId: string;
  branchCode: string;
  bankAccountNumber: string;
  accountNumber: string;
  isAccountLocked: boolean;
  isBranchesLoading: boolean;
  hasBranchLookup: boolean;
  bankProviders: BankProviderInfo[];
  bankBranches: BankBranchInfo[];
  isFlashing: (field: string) => boolean;
  fieldAi: ReturnType<typeof useFieldAiProps>;
  onBankProviderChange: (providerId: string) => void;
  onBranchCodeChange: (value: string) => void;
  onBankAccountNumberChange: (value: string) => void;
  onAccountNumberChange: (value: string) => void;
  accountNumberError: string | undefined;
  bankCodeError: string | undefined;
  branchCodeError: string | undefined;
  bankAccountNumberError: string | undefined;
}

/** Either the 3-field bank/branch/account grid or a single IBAN input (nothing for a check) — shared by beneficiary, municipality, and RMI payees. */
export function BankAccountFields({
  accountType,
  bankProviderId,
  branchCode,
  bankAccountNumber,
  accountNumber,
  isAccountLocked,
  isBranchesLoading,
  hasBranchLookup,
  bankProviders,
  bankBranches,
  isFlashing,
  fieldAi,
  onBankProviderChange,
  onBranchCodeChange,
  onBankAccountNumberChange,
  onAccountNumberChange,
  accountNumberError,
  bankCodeError,
  branchCodeError,
  bankAccountNumberError
}: BankAccountFieldsProps) {
  const {t} = useTranslation('trustAccounts');

  if (isBankCheckAccountType(accountType)) return null;

  return (
    <>
      {accountType === BankAccountIdentifierType.BANK_DETAILS ? (
        <div className="grid grid-cols-3 gap-3">
          <AiFlashWrapper field="bankCode" fieldAi={fieldAi} isFlashing={isFlashing}>
            {ai => (
              <BankProviderField
                label={t(`${PREFIX}.bankCode`)}
                placeholder={t(`${PREFIX}.bankCodePlaceholder`)}
                bankNumberLabel={t(`${PREFIX}.bankNumberLabel`)}
                providers={bankProviders}
                value={bankProviderId}
                onValueChange={val => {
                  ai.onAiChange();
                  onBankProviderChange(val);
                  onBranchCodeChange('');
                }}
                startAdornment={<Icon name="building-08" width={18} height={18} />}
                cornerBadge={ai.aiIndicator}
                loading={ai.aiGenerating}
                emptyText={t(`${PREFIX}.bankEmpty`)}
                error={bankCodeError}
                disabled={isAccountLocked}
                required
              />
            )}
          </AiFlashWrapper>
          <AiFlashWrapper field="branchCode" fieldAi={fieldAi} isFlashing={isFlashing}>
            {ai => (
              <BankBranchField
                label={t(`${PREFIX}.branchCode`)}
                placeholder={t(`${PREFIX}.branchCodePlaceholder`)}
                branches={bankBranches}
                hasBranchLookup={hasBranchLookup}
                value={branchCode}
                onValueChange={val => {
                  ai.onAiChange();
                  onBranchCodeChange(val);
                }}
                cornerBadge={ai.aiIndicator}
                disabled={isAccountLocked || !bankProviderId}
                loading={isBranchesLoading || ai.aiGenerating}
                emptyText={t(`${PREFIX}.branchEmpty`)}
                error={branchCodeError}
                required
              />
            )}
          </AiFlashWrapper>
          <AiFlashWrapper field="bankAccountNumber" fieldAi={fieldAi} isFlashing={isFlashing}>
            {ai => (
              <FieldInput
                label={t(`${PREFIX}.bankAccountNumber`)}
                placeholder={t(`${PREFIX}.bankAccountNumberPlaceholder`)}
                value={bankAccountNumber}
                onChange={e => {
                  ai.onAiChange();
                  onBankAccountNumberChange(e.target.value);
                }}
                cornerBadge={withDebugFieldChange(
                  ai.aiIndicator,
                  onBankAccountNumberChange,
                  generateBankAccountNumber,
                  ai.onAiChange
                )}
                isLoading={ai.aiGenerating}
                error={bankAccountNumberError}
                disabled={isAccountLocked}
                required
              />
            )}
          </AiFlashWrapper>
        </div>
      ) : (
        <AiFlashWrapper field="accountNumber" fieldAi={fieldAi} isFlashing={isFlashing}>
          {ai => (
            <FieldInput
              label={t(`${PREFIX}.accountNumber`)}
              placeholder={t(`${PREFIX}.accountNumberPlaceholderIban`)}
              startAdornment={<Icon name="bank-branch" width={18} height={18} />}
              cornerBadge={withDebugFieldChange(
                ai.aiIndicator,
                onAccountNumberChange,
                generateIsraeliIban,
                ai.onAiChange
              )}
              isLoading={ai.aiGenerating}
              value={accountNumber}
              onChange={e => {
                ai.onAiChange();
                onAccountNumberChange(e.target.value);
              }}
              error={accountNumberError}
              disabled={isAccountLocked}
              required
            />
          )}
        </AiFlashWrapper>
      )}
    </>
  );
}

export interface AccountTypeFieldProps {
  accountType: CreditorAccountType;
  accountTypeOptions: FieldSelectOption[];
  isZahavOnly: boolean;
  fieldAi: ReturnType<typeof useFieldAiProps>;
  onAccountTypeChange: (value: string) => void;
}

/** The payment-method selector — rendered ahead of the document so a check, which needs none, never shows one. */
export function AccountTypeField({
  accountType,
  accountTypeOptions,
  isZahavOnly,
  fieldAi,
  onAccountTypeChange
}: AccountTypeFieldProps) {
  const {t} = useTranslation('trustAccounts');
  const accountNumberAi = fieldAi('accountNumber');

  return (
    <FieldSelect
      label={t(`${PREFIX}.accountType`)}
      value={accountType}
      cornerBadge={accountNumberAi.aiIndicator}
      isLoading={accountNumberAi.aiGenerating}
      onValueChange={val => {
        accountNumberAi.onAiChange();
        onAccountTypeChange(val);
      }}
      options={accountTypeOptions}
      isLocked={isZahavOnly}
      lockedLabel={t(`${PREFIX}.accountTypeFixedByAmount`)}
      required
    />
  );
}
